<template>
    <div class="general__dropdown" :class="{ 'is-selected': modelValue !== '' }">
        <select
            ref="select"
            class="postform"
            :id="id"
            :aria-labelledby="activeLabelId || undefined"
            data-vue-select
            :value="modelValue"
            :disabled="disabled"
            @change="onChange"
        >
            <option value="">{{ allLabel }}</option>
            <option v-for="term in terms" :key="term.slug" :value="term.slug">
                {{ term.name }}
            </option>
        </select>
        <span v-if="!labelledBy && label" :id="fallbackLabelId" class="vh">{{ label }}</span>
        <button
            v-if="modelValue !== '' && !disabled"
            type="button"
            class="general__dropdown__clear"
            @click="clear"
        >
            <span class="vh">{{ clearLabel }}</span>
            <span aria-hidden="true" v-html="icons.close"></span>
        </button>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
    modelValue: { type: String, default: '' },
    terms: { type: Array, default: () => [] },
    id: { type: String, required: true },
    label: { type: String, default: '' },
    labelledBy: { type: String, default: '' },
    allLabel: { type: String, default: '' },
    clearLabel: { type: String, default: '' },
    clearUrl: { type: String, default: '' },
    icons: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const select = ref(null)

const fallbackLabelId = computed(() => `${props.id}-label`)

const activeLabelId = computed(() => props.labelledBy || (props.label ? fallbackLabelId.value : ''))

const onChange = (event) => {
    emit('update:modelValue', event.target.value)
}

const focusControl = () => {
    const selection = select.value?.parentElement?.querySelector('.select2-selection')

    if (selection instanceof HTMLElement) {
        selection.focus()
        return
    }

    select.value?.focus()
}

const clear = () => {
    focusControl()
    emit('update:modelValue', '')
}

const onSelectionKeydown = (event) => {
    if (!props.modelValue || (event.key !== 'Backspace' && event.key !== 'Delete')) {
        return
    }

    if (event.currentTarget.getAttribute('aria-expanded') === 'true') {
        return
    }

    event.preventDefault()
    clear()
}

const labelSelect2 = () => {
    if (!activeLabelId.value || !select.value) {
        return
    }

    const selection = select.value.parentElement?.querySelector('.select2-selection')

    if (!(selection instanceof HTMLElement)) {
        return
    }

    const renderedId = selection.querySelector('.select2-selection__rendered')?.id

    selection.setAttribute(
        'aria-labelledby',
        renderedId ? `${activeLabelId.value} ${renderedId}` : activeLabelId.value
    )
    selection.removeEventListener('keydown', onSelectionKeydown)
    selection.addEventListener('keydown', onSelectionKeydown)
}

const $ = window.jQuery

const hasSelect2 = () => $ && typeof $.fn.select2 === 'function'

const bindSelect2 = () => {
    if (!hasSelect2() || !select.value) {
        return
    }

    const $select = $(select.value)

    if ($select.hasClass('select2-hidden-accessible')) {
        $select.select2('destroy')
    }

    if (props.disabled) {
        return
    }

    $select.select2({
        width: '100%',
        matcher(params, data) {
            const term = (params.term || '').trim()

            if (term === '') {
                return data
            }

            if (typeof data.text === 'undefined') {
                return null
            }

            const haystack = data.text.toLowerCase()
            const needles = term.toLowerCase().split(/\s+/).filter(Boolean)

            return needles.every((needle) => haystack.includes(needle)) ? data : null
        },
    })

    $select.on('select2:select select2:clear', () => {
        select.value.dispatchEvent(new Event('change', { bubbles: true }))
    })

    labelSelect2()
}

onMounted(() => {
    bindSelect2()
})

watch(
    () => [props.terms, props.disabled],
    async () => {
        await nextTick()
        bindSelect2()
    },
    { deep: true }
)

watch(() => props.modelValue, (value) => {
    if (!hasSelect2() || !select.value || props.disabled) {
        return
    }

    $(select.value).val(value).trigger('change.select2')
})

onBeforeUnmount(() => {
    if (hasSelect2() && select.value && $(select.value).hasClass('select2-hidden-accessible')) {
        $(select.value).select2('destroy')
    }
})
</script>
